import React, { useState } from 'react';
import { Form, Select, Input, Button, Space, Alert, DatePicker } from 'antd';
import { SupplierOrder, OrderStatus } from '../types.ts';
import { getSelectableSupplierOrderStatusIds } from '../utils/supplierOrderStatus.ts';

const { Option } = Select;
const { TextArea } = Input;

interface SupplierOrderStatusModalProps {
  order: SupplierOrder | null;
  orderStatuses: OrderStatus[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

const SupplierOrderStatusModal: React.FC<SupplierOrderStatusModalProps> = ({
  order,
  orderStatuses,
  onSubmit,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [selectedStatusId, setSelectedStatusId] = useState<number>();

  // Stati selezionabili = stato attuale + transizioni manuali autorizzate (util condiviso,
  // coerente con il vincolo backend validate_status_sequence). Gli altri NON sono mostrati.
  const allAvailableStatuses = React.useMemo(() => {
    if (!order || !order.order_status) {
      return orderStatuses;
    }
    const selectableIds = getSelectableSupplierOrderStatusIds(order.order_status_id);
    return orderStatuses.filter(status => selectableIds.includes(status.id));
  }, [order, orderStatuses]);

  // Early return dopo tutti i hooks
  if (!order) return null;
  const handleSubmit = (values: any) => {
    const statusData = {
      ...values,
      delivery_date: values.delivery_date ? values.delivery_date.format('YYYY-MM-DD') : null,
    };
    onSubmit(statusData);
  };

  const getStatusDescription = (statusId: number) => {
    const status = orderStatuses.find(s => s.id === statusId);
    if (!status) return '';

    const descriptions: { [key: string]: string } = {
      'Nuovo': 'L\'ordine è stato registrato e inviato al fornitore.',
      'In Attesa': 'Ordine in attesa di conferma del fornitore (stato in cui nascono gli ordini generati dalla trasformazione di un ordine cliente).',
      'In Lavorazione': 'Il fornitore sta elaborando l\'ordine.',
      'Confermato': 'Il fornitore ha confermato l\'ordine e i tempi di consegna. I costi delle righe aggiornano il listino costi dei prodotti.',
      'In Produzione': 'I prodotti sono in produzione presso il fornitore: le quantità vengono caricate in Inventario come In Produzione.',
      'Pronto per Spedizione': 'La merce è pronta: l\'ordine diventa selezionabile nella creazione delle spedizioni.',
      'Spedito': 'Il fornitore ha spedito la merce (impostato automaticamente dalla Gestione Spedizioni).',
      'In Transito': 'La merce è in viaggio verso la destinazione (automatico).',
      'Consegnato': 'La merce è arrivata a destinazione (automatico).',
      'Evaso': 'Ordine chiuso: tutta la merce ordinata è stata spedita e caricata in magazzino.',
      'Annullato': 'L\'ordine è annullato (stato definitivo). Nessuna quantità era ancora in produzione.'
    };

    return descriptions[status.status_name] || `Stato: ${status.status_name}`;
  };

  const requiresDeliveryDate = (statusId: number) => {
    const status = orderStatuses.find(s => s.id === statusId);
    return status?.status_name === 'Confermato';
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        order_status_id: order.order_status_id,
      }}
    >
      <Alert
        message={`Ordine Fornitore: ${order.order_number}`}
        description={`Fornitore: ${order.supplier?.name} - Stato attuale: ${order.order_status?.status_name || 'N/A'}`}
        type="info"
        style={{ marginBottom: 16 }}
      />

      <Form.Item
        label="Stato Attuale"
      >
        <Input value={order.order_status?.status_name} disabled />
      </Form.Item>

      <Form.Item
        name="order_status_id"
        label="Nuovo Stato"
        rules={[{ required: true, message: 'Seleziona un nuovo stato' }]}
      >
        <Select
          placeholder="Seleziona nuovo stato"          onChange={(value) => {
            setSelectedStatusId(value);
            form.setFieldsValue({ order_status_id: value });
          }}
        >
          {allAvailableStatuses.map(status => (
            <Option key={status.id} value={status.id}>
              {status.status_name}
              {status.id === order.order_status_id && ' (Attuale)'}
            </Option>
          ))}
        </Select>      </Form.Item>

      {selectedStatusId && selectedStatusId !== order.order_status_id && (
        <Alert
          message="Descrizione Stato"
          description={getStatusDescription(selectedStatusId)}
          type="info"
          style={{ marginBottom: 16 }}
        />
      )}

      {selectedStatusId && requiresDeliveryDate(selectedStatusId) && (
        <Form.Item
          name="delivery_date"
          label="Data Consegna Confermata"
          rules={[{ required: true, message: 'Data consegna richiesta per questo stato' }]}
        >
          <DatePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            placeholder="Seleziona data consegna"
          />
        </Form.Item>
      )}

      {selectedStatusId && orderStatuses.find(s => s.id === selectedStatusId)?.status_name === 'In Produzione' && (
        <Alert
          message="Attenzione"
          description="Passando a 'In Produzione' le quantità dell'ordine vengono caricate in Inventario come 'In Produzione' (con lo Stock Code dell'ordine) e diventano prenotabili dagli ordini cliente. Da questo momento le righe dell'ordine non sono più modificabili e l'ordine non è più eliminabile: per annullare la merce non spedita si usa 'Annulla Residuo'."
          type="warning"
          style={{ marginBottom: 16 }}
        />
      )}

      {selectedStatusId && orderStatuses.find(s => s.id === selectedStatusId)?.status_name === 'Annullato' && (
        <Alert
          message="Attenzione"
          description="'Annullato' è uno stato definitivo: l'ordine non potrà più avanzare."
          type="warning"
          style={{ marginBottom: 16 }}
        />
      )}

      <Form.Item
        name="notes"
        label="Note (opzionale)"
      >
        <TextArea
          rows={3}
          placeholder="Aggiungi note sul cambio di stato..."
        />
      </Form.Item>

      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit">
            Aggiorna Stato
          </Button>
          <Button onClick={onCancel}>
            Annulla
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default SupplierOrderStatusModal;